import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import * as dayjs from "dayjs";
import { useFetch } from "../../hooks";
import { getLaunchStatus, getStatusColor, getLocation } from '../../controllers'
import Modal from '../Modal/Modal'
import './EnhancedTable.css'

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  { id: 'no', numeric: true, disablePadding: true, label: 'No.' },
  { id: 'launched', numeric: true, disablePadding: false, label: 'Launched (UTC)' },
  { id: 'location', numeric: true, disablePadding: false, label: 'Location' },
  { id: 'mission', numeric: true, disablePadding: false, label: 'Mission' },
  { id: 'orbit', numeric: true, disablePadding: false, label: 'Orbit' },
  { id: 'launchStatus', numeric: true, disablePadding: false, label: 'Launch Status' },
  { id: 'rocket', numeric: true, disablePadding: false, label: 'Rocket' },
];

function EnhancedTableHead(props) {

  return (
    <TableHead>
      <TableRow className='table_Head'>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align='center'
            className='tablePadding'
          >
              {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}));

function EnhancedTable({ callApi, launch }) {
  let { response, loading } = useFetch(callApi, {}, [launch]);
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState();
  const classes = useStyles();
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('calories');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = response.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  const togglePopup = (res) => {
    setIsOpen(!isOpen);
    setData(res)
  }
  
  if (loading)
    return (
      <section className="h-screen">
        <div className="loader" />
    </section>
    );

  if ((response || []).length === 0)
    return (
        <section className="text-gray-500 body-font bg-gray-900 h-screen">
            <div className="container px-5 py-24 mx-auto">
                <div className="flex flex-col text-center w-full">
                    <h1 className="text-xl font-medium title-font mb-4">
                        No results found for the filter
                    </h1>
                </div>
            </div>
        </section>
    );

  return (
    <div className={classes.root}>
      {isOpen && <Modal
        handleClose={togglePopup}
        data={data}
      />}
      <Paper className={classes.paper}>
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              numSelected={response.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={response.length}
            />
            <TableBody>
              {stableSort(response, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((response, index) => {
                  const isItemSelected = isSelected(response.name);

                  return (
                    <TableRow
                      hover
                      aria-checked={isItemSelected}
                      onClick={() => togglePopup(response)}
                      tabIndex={-1}
                      key={response.name}
                      selected={isItemSelected}
                    > 
                      <TableCell align="center">{response.flight_number}</TableCell>
                      <TableCell align="center">
                        {dayjs(response.launch_date_local).format(
                            "D MMMM YYYY at h:mm"
                        )}
                      </TableCell>
                      <TableCell align="center">{getLocation(response)}</TableCell>
                      <TableCell align="center">{response.name}</TableCell>
                      <TableCell align="center">{response.tbd === true ? "ISS" : "LEO"}</TableCell>
                      <TableCell align="center"><button className={`statusButton ${getStatusColor(response)}`}>{getLaunchStatus(response)}</button></TableCell>
                      <TableCell align="center">{response.rocket === '5e9d0d95eda69955f709d1eb' ? "Falcon 8" : "Falcon 9"}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 15, 25]}
          component="div"
          count={-1}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
}


EnhancedTable.defaultProps = {
  callApi: "https://api.spacexdata.com/v3/launches",
};

export default EnhancedTable;